import { BuildingOffice2Icon, ChevronDownIcon, ClockIcon, GlobeAltIcon, ShareIcon, Squares2X2Icon, UsersIcon } from '@heroicons/react/16/solid';
import { OrganizationSwitcher, useAuth, useOrganization, useOrganizationList } from '@clerk/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import clsx from 'clsx';
import { Avatar } from '../ui/avatar';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownHeading,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSection,
  DropdownTick,
} from '../ui/dropdown';
import { SidebarItem, SidebarLabel } from '../ui/sidebar';
import { useDirectory } from '../lib/directory';
import { useSession } from '../lib/session';
import { BRAND_AVATAR } from './account';
import { Mark } from './Mark';

/**
 * Where you are, and how you move sideways from it: the organisation and
 * site you have open are switchers at the top of the sidebar rather than
 * lists you navigate back to.
 *
 * The SITE half is ours and reads the shell's directory, so its menu is
 * already populated by the time you open it. The ORGANISATION half is
 * Clerk's own `<OrganizationSwitcher />` (#97 phase 4) — see below.
 */

/** A menu row that stands in for the list: loading, failed, or empty. */
function MenuNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="col-span-full px-3.5 py-1.5 text-sm/6 text-fg-2 sm:px-3">{children}</div>
  );
}

/**
 * SidebarItem's shape without its behaviour, for a row that only names
 * where you are. The hover and active styles are deliberately absent —
 * nothing happens, so nothing should suggest it might.
 */
const STATIC_ROW = clsx(
  'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-medium text-fg sm:py-2 sm:text-sm/5',
  '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7 sm:*:data-[slot=avatar]:size-6'
);

/**
 * STATIC_ROW plus the press affordances, for Clerk's trigger.
 *
 * Built from the same string rather than written out again: Clerk's
 * button and `SidebarItem` sit one above the other in the sidebar, so
 * any drift between them shows as two rows that nearly line up. The
 * hover colours are SidebarItem's, spelled with plain `hover:` because
 * Clerk's button is not a Headless UI one and carries no `data-hover`.
 */
const TRIGGER_ROW = clsx(
  STATIC_ROW,
  'justify-start hover:bg-wash',
  // STATIC_ROW's avatar rule again, reached a different way. Clerk's
  // avatar is not a direct child of the button — it sits inside a
  // preview wrapper — so the `*:data-[slot=avatar]` rule above cannot
  // see it, and `cl-avatarBox` is the public name Clerk gives it. Same
  // size, same 20% corner, same -0.5 overhang so the name lines up with
  // the site row's; and the wrapper's own gap brought down to the row's.
  '[&_.cl-avatarBox]:-m-0.5 [&_.cl-avatarBox]:size-7 [&_.cl-avatarBox]:rounded-[20%] sm:[&_.cl-avatarBox]:size-6',
  '[&_.cl-organizationPreview]:gap-3 [&_.cl-userPreview]:gap-3'
);

/**
 * One width for both menus.
 *
 * Clerk draws its own popover and would otherwise size it to its own
 * content, so the two menus under two adjacent triggers would open at
 * two different widths. Pinning ours and telling Clerk the same number
 * is the only way to make them agree — matching by eye against Clerk's
 * default would break the day Clerk changes it.
 *
 * Width only. The popover's COLOURS come from the provider, not from
 * here — `lib/clerk-appearance.ts` hands Clerk the console's palette for
 * whichever scheme is showing, so the login card and this menu are
 * painted once and alike.
 */
export const MENU_WIDTH = 'w-72';

/**
 * The shape of a switcher row, before it can say anything true.
 *
 * Both switchers know where they are long before they know what it is
 * CALLED — Clerk has to hydrate, and the directory is a fetch — and both
 * used to fill that gap with a real row built from whatever was to hand:
 * the org fell back to the label below, the site to the key in the URL.
 * Each then swapped for the real thing a moment later, which is the
 * flicker: two rows drawn per navigation, and the first one wrong.
 *
 * Holding the row's exact geometry matters more than the shimmer. Same
 * padding, same avatar size, same line height, so the real content lands
 * in place rather than nudging the sidebar as it arrives.
 */
function SwitcherSkeleton({ label }: { label: string }) {
  return (
    <div className={clsx(STATIC_ROW, 'animate-pulse')} role="status" aria-label={label}>
      <div className="rounded-lg size-7 shrink-0 rounded-md bg-tint sm:size-6" />
      <div className="h-3 w-28 rounded-full bg-tint" />
    </div>
  );
}

/**
 * Where you are, named, with nothing to press.
 *
 * Not a fallback for a slow list but for a missing IDENTITY PROVIDER:
 * both paths below that reach it have no Clerk session to read a
 * membership list out of, and inventing a switcher from our own directory
 * would be the very component this phase replaced. Naming the
 * organisation is the part that must not be lost — the mark and the name
 * are how you know which one you are looking at — so that is what stays.
 */
function OrgLabel() {
  const { client } = useParams();
  const { clients } = useDirectory();
  const current = clients?.find((c) => c.id === client);
  return (
    <div className={STATIC_ROW}>
      {client ? (
        <Mark id={client} name={current?.name} image={current?.settings?.logo} fallbackClassName={BRAND_AVATAR} />
      ) : (
        <Avatar square initials="h" alt="" className={BRAND_AVATAR} />
      )}
      <SidebarLabel className="font-semibold">{current?.name ?? client ?? 'headless'}</SidebarLabel>
    </div>
  );
}

/**
 * Point Clerk's ACTIVE ORGANISATION at the one in the URL.
 *
 * This is what lets phase 4 land before phase 3. The console still reads
 * the organisation from `/c/:client` and authorisation is still ours, out
 * of D1 — nothing here changes either. But Clerk's switcher renders the
 * active organisation of the SESSION, which is Clerk's own state and has
 * no idea what our router is doing. Left alone it would sit on whichever
 * organisation Clerk last made active — or on the personal account — and
 * calmly name the wrong one while the page beside it showed another. A
 * switcher that misreports where you are is worse than no switcher.
 *
 * So the URL leads and Clerk follows, not the other way round. What it
 * follows BY is `clerk_org_id` — the org id recorded on the client when
 * the pair was made — and not the slug.
 *
 * The slug looked like the free answer, because the API asks for one:
 * `POST /v1/clients` sends `slug: client.id`. But that only holds for
 * organisations this code created. An organisation made by hand in the
 * dashboard, or made before #98 shipped the slug at all, carries a slug
 * Clerk invented from the NAME — `ridge.media` became
 * `ridge-media-1788004789863934135` — and a `setActive` keyed on the
 * client id then silently matches nothing at all. The id is the only
 * half of the pair we control and store, so the join belongs on it.
 *
 * A client with NO recorded organisation clears the active one instead of
 * leaving it. Nothing here can name the right org, and the alternative is
 * Clerk's switcher confidently displaying whichever org happened to be
 * active last — a switcher that misreports where you are being the very
 * thing this effect exists to prevent.
 *
 * A rejection is expected rather than exceptional, and is swallowed on
 * purpose: `setActive` refuses an organisation you are not a member of IN
 * CLERK, which is precisely a `super_admin` on a client they have not
 * been added to by hand. They keep the page — our authorisation never
 * asked Clerk — and the switcher keeps whatever it had. The effect does
 * not retry: its dependencies have not changed, so it does not re-run.
 */
function useClerkOrgFromUrl() {
  const { client } = useParams();
  const { clients } = useDirectory();
  const { isLoaded: orgLoaded, organization } = useOrganization();
  const { isLoaded, setActive } = useOrganizationList();
  // `undefined` while the directory is still loading, which is NOT the
  // same as a client with no organisation: the first waits, the second
  // clears. Reading the row rather than the id keeps them apart.
  const row = clients?.find((c) => c.id === client);
  const target = row ? (row.clerk_org_id ?? null) : undefined;
  const active = organization?.id ?? null;
  useEffect(() => {
    // `/` and `/tokens` name no organisation. Deliberately NOT cleared
    // there: nothing on those pages reads the active organisation, and
    // clearing it would cost a round trip and a flicker on the way to the
    // organisation you are about to pick out of the switcher.
    if (!client || target === undefined || !orgLoaded || !isLoaded || !setActive) return;
    if (active === target) return;
    void setActive({ organization: target }).catch(() => {});
  }, [client, target, orgLoaded, isLoaded, setActive, active]);
}

/**
 * Clerk's organisation switcher, wearing the sidebar's clothes.
 *
 * Two things are deliberately taken away, and both are about the halves
 * of Clerk's organisation model that we have NOT adopted:
 *
 * - **Create organisation.** An organisation here is a `clients` row that
 *   `H.clientCreate` mirrors into Clerk — it makes both, in that order.
 *   One made in Clerk would have no row behind it, so the `/c/<slug>` this
 *   very component then navigates to would lead nowhere.
 *
 * - **Manage organisation.** Clerk's organisation profile can add and
 *   remove members, and until phase 3 flips the authorisation read a Clerk
 *   membership grants nothing and a D1 membership grants everything. An
 *   admin who removed somebody there would believe they had revoked access
 *   and would not have. Members are managed on `/c/:client/members`, which
 *   writes both (phase 2).
 *
 * The trigger is styled to match `SidebarItem` because it is always on
 * screen. Its text colour is NOT settled here, though: Clerk colours the
 * organisation's name itself rather than inheriting from the button, so
 * the row's own `text-fg` never reached it and the name sat dark on
 * the dark sidebar. That colour, and the popover's, come from the palette
 * `App.tsx` hands the provider (`lib/clerk-appearance.ts`), which follows
 * the console's scheme.
 */
function ClerkOrgSwitcher() {
  const { isLoaded, isSignedIn } = useAuth();
  const { clients } = useDirectory();
  useClerkOrgFromUrl();
  // Two states that look alike and are not. Clerk not having spoken YET
  // is a wait, and drawing a real row into it means drawing a row that is
  // replaced a moment later — the flicker on every navigation. A skeleton
  // says "not yet" without asserting anything false.
  //
  // Being signed in WITHOUT Clerk is not a wait; it is the permanent
  // condition of staff who arrived through the GitHub device flow, which
  // mints no Clerk identity. Nothing further is coming, so a skeleton
  // there would pulse for the whole session. That one still gets the
  // label: being unable to switch is not being unable to see where
  // you are.
  if (!isLoaded) return <SwitcherSkeleton label="Loading organisation" />;
  if (!isSignedIn) return <OrgLabel />;
  return (
    <OrganizationSwitcher
      // The same join as the effect above, in the other direction: the
      // organisation Clerk hands back is found among OUR clients by its
      // id, and the route is that client's id. Reading `org.slug` here
      // instead — which is what Clerk's `:slug` template would give —
      // sends anyone whose slug drifted to `/c/ridge-media-1788004…`,
      // a client id that does not exist, which is a dead end reached by
      // pressing the control that exists to avoid them.
      //
      // An organisation with no client behind it falls to the
      // organisations list. That is a Clerk org somebody made by hand:
      // there is no `/c/…` for it, and the list at least says so.
      afterSelectOrganizationUrl={(org) => {
        const mine = clients?.find((c) => c.clerk_org_id === org.id);
        return mine ? `/c/${mine.id}` : '/';
      }}
      // Personal is KEPT, spelled out rather than left to the default,
      // because it is carrying a meaning of ours. It is the closest thing
      // Clerk has to "no organisation chosen", which is exactly what `/`
      // is — the organisations list and the fleet-wide pages beside it —
      // and it is the only way back to that list, which the old menu
      // spelled "All organisations".
      hidePersonal={false}
      afterSelectPersonalUrl="/"
      appearance={{
        elements: {
          rootBox: 'w-full',
          organizationSwitcherTrigger: TRIGGER_ROW,
          // SidebarItem's trailing-icon rule, for the chevron: pushed to
          // the far edge so the row spans the sidebar like the site
          // switcher's rather than stopping where the name does.
          organizationSwitcherTriggerIcon: 'ml-auto size-5 shrink-0 sm:size-4',
          // The name, at SidebarLabel's size and weight. Clerk sets its
          // own on the span and colours the personal account's as
          // secondary text, so the row's own `text-fg` never
          // reached it. These two keys also name the rows inside the
          // popover, which is fine: DropdownLabel is the same size.
          organizationPreviewMainIdentifier: 'truncate text-base/6 font-medium sm:text-sm/5',
          userPreviewMainIdentifier: 'truncate text-base/6 font-medium sm:text-sm/5',
          userPreviewMainIdentifier__personalWorkspace: 'text-fg',
          // The same width the site menu below opens at. Clerk would
          // otherwise size this to its own content, and two menus under
          // two adjacent triggers opening at two different widths reads
          // as a mistake even when nothing is wrong.
          organizationSwitcherPopoverCard: MENU_WIDTH,
          organizationSwitcherPopoverActionButton__createOrganization: { display: 'none' },
          organizationSwitcherPopoverActionButton__manageOrganization: { display: 'none' },
        },
      }}
    />
  );
}

/**
 * The organisation you have open, and the way to another one.
 *
 * Clerk's, not ours (#97 phase 4). What that buys is one list rather than
 * two: the organisations you can switch between are the Clerk
 * organisations you are a member of, which is the same fact phase 2
 * already mirrors on every membership change, so there is nothing here to
 * drift out of step with the API.
 *
 * What it costs is written where it is lost — `OrgLabel` for the sessions
 * Clerk cannot see, and `ClerkOrgSwitcher` for the parts of Clerk's
 * organisation model we do not want. Two more go here rather than there:
 *
 * - The org's `settings.logo` and the initials-and-tint fallback seeded on
 *   the client id are superseded by Clerk's own name and logo. Both still
 *   render on the organisations page and in organisation settings, which
 *   are ours; only this row stops reading them.
 * - "A single membership is a label, not a dropdown" goes with them.
 *   Clerk's switcher is a dropdown whether or not there is a second
 *   organisation in it. The related property — that a link to an
 *   organisation you are NOT in must not be a dead end — survives, and by
 *   a different route: Clerk lists the organisations you belong to, so
 *   yours are in the menu whatever the URL says.
 *
 * ClerkProvider is CONDITIONAL — `App.tsx` mounts it only when the
 * environment reports a publishable key, and an environment with only
 * GitHub staff login runs the console without Clerk entirely. Clerk's
 * components throw outside their provider, so this asks before mounting
 * one rather than discovering it as a white page.
 */
export function OrgSwitcher() {
  const { config } = useSession();
  if (!config?.clerk_publishable_key) return <OrgLabel />;
  return <ClerkOrgSwitcher />;
}

/** The sites of the open organisation. Only the shell's organisation
 *  scope renders it, so `client` is always set here. */
/** Above this many, finding one in the menu is work rather than a glance. */
const SITE_FILTER_FROM = 5;

export function SiteSwitcher() {
  const { client = '', key } = useParams();
  const { sites, sitesError } = useDirectory();
  const open = sites?.find((s) => s.key === key);
  const [query, setQuery] = useState('');
  // The key is in the URL from the first paint, but what the site CALLS
  // itself is in its bundle and arrives with the directory — so a row
  // drawn now says `read-a` and a row drawn a moment later says
  // `The Read`. Same flicker as the organisation above, same answer.
  // An ERROR is not a wait: the menu below says so and the trigger falls
  // back to the key, which is true and all we will ever have.
  if (sites === null && !sitesError) return <SwitcherSkeleton label="Loading sites" />;
  const needle = query.trim().toLowerCase();
  // Matches what the row SHOWS (the name) as well as what it is keyed and
  // addressed by, so typing either finds it.
  const shown = needle
    ? (sites ?? []).filter((s) => `${s.name ?? ''} ${s.key} ${s.domain}`.toLowerCase().includes(needle))
    : sites;
  return (
    <Dropdown>
      <DropdownButton as={SidebarItem} aria-label="Switch site">
        {key ? <Mark id={key} name={open?.name} image={open?.mark} fallbackClassName={BRAND_AVATAR} plate={false} /> : <GlobeAltIcon />}
        {/* What the site calls itself, falling back to the key it is
            addressed by — a site with no bundle yet has no name. */}
        <SidebarLabel>{key ? (open?.name ?? key) : 'All sites'}</SidebarLabel>
        <ChevronDownIcon />
      </DropdownButton>
      {/* A fixed width, not a minimum: the organisation menu above is
          Clerk's and takes a number, so ours has to be a number too or
          the two only agree when the content happens to make them. */}
      <DropdownMenu className={MENU_WIDTH} anchor="bottom start">
        <DropdownSection>
          <DropdownHeading>Sites</DropdownHeading>
          {/* No "Loading…" row: the trigger is a skeleton until the list
              resolves, so this menu cannot be opened while it is still
              in flight. */}
          {sitesError ? <MenuNote>Couldn’t load sites</MenuNote> : null}
          {sites?.length === 0 ? <MenuNote>No sites yet</MenuNote> : null}
          {/* Only once the list is long enough to search. Below that the
              control is more to read than the list it filters — the same
              threshold the Sites page uses, for the same reason.

              stopPropagation on keydown: the menu treats typing as
              type-ahead and would steal the keystrokes and move the
              selection while you filled this in. */}
          {(sites?.length ?? 0) > SITE_FILTER_FROM ? (
            // col-span-full like every other structural child of this
            // menu. The menu is a grid — auto,1fr,… — and a child without
            // it lands in column ONE, the icon column, sizing that column
            // to this input's padding box and indenting every row's label
            // to match. MenuNote, DropdownItem, DropdownHeading and
            // DropdownDivider all span; so must this.
            <div className="col-span-full px-3.5 pt-1 pb-2 sm:px-3">
              <input
                type="text"
                autoFocus
                aria-label="Filter sites"
                placeholder="Filter sites"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="w-full rounded-md border border-line bg-control px-2 py-1 text-sm/6 text-fg placeholder:text-fg-3 hover:border-line-hover focus:border-focus focus:outline-hidden"
              />
            </div>
          ) : null}
          {shown?.length === 0 && (sites?.length ?? 0) > 0 ? (
            <MenuNote>No site matches “{query.trim()}”.</MenuNote>
          ) : null}
          {shown?.map((s) => (
            <DropdownItem key={s.key} href={`/c/${client}/s/${s.key}`}>
              <Mark id={s.key} name={s.name} image={s.mark} fallbackClassName={BRAND_AVATAR} plate={false} />
              <DropdownLabel>{s.name ?? s.key}</DropdownLabel>
              {s.key === key ? <DropdownTick /> : null}
            </DropdownItem>
          ))}
        </DropdownSection>
        <DropdownDivider />
        {/* The organisation's own pages. Inside a site the sidebar shows
            only the site's navigation (Shell.tsx), so this menu, right
            under the organisation's name, is where the organisation's
            pages are. Clerk's switcher above cannot carry them: its
            popover takes no items of ours. */}
        <DropdownSection>
          <DropdownHeading>Organisation</DropdownHeading>
          <DropdownItem href={`/c/${client}`}>
            <Squares2X2Icon />
            <DropdownLabel>All sites</DropdownLabel>
          </DropdownItem>
          <DropdownItem href={`/c/${client}/members`}>
            <UsersIcon />
            <DropdownLabel>Members</DropdownLabel>
          </DropdownItem>
          <DropdownItem href={`/c/${client}/content-network`}>
            <ShareIcon />
            <DropdownLabel>Content network</DropdownLabel>
          </DropdownItem>
          <DropdownItem href={`/c/${client}/activity`}>
            <ClockIcon />
            <DropdownLabel>Activity</DropdownLabel>
          </DropdownItem>
          <DropdownItem href={`/c/${client}/settings`}>
            <BuildingOffice2Icon />
            <DropdownLabel>Organisation settings</DropdownLabel>
          </DropdownItem>
        </DropdownSection>
      </DropdownMenu>
    </Dropdown>
  );
}
